import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, BookOpen, CheckCircle2 } from "lucide-react";
import { track, events } from "../lib/analytics";
import { getRoutingConfig, openAffiliatePartner } from "../lib/partnerRouting";
import {
  getFunnelCopy,
  CATEGORY_COMPARE_DESTINATIONS,
} from "../lib/categoryCopy";
import StayConnectedForm from "./intake/StayConnectedForm";
import TrustChips from "./intake/TrustChips";
import FunnelProgress from "./funnel/FunnelProgress";
import { INSURIFY_SAVINGS_SHORT } from "../lib/partnerClaims";

// Iter-114 — Extracted from QuoteForm.jsx (was 699 LoC; now 365). This
// component owns the entire post-submit conversion surface: truthful
// success copy, optional email capture (via the idempotent /api/newsletter
// → Resend pipeline), category-aware "Compare Options" routing
// (affiliate-link vs lead-capture navigate), and the iter-110
// insurance-only loyalty-tax guide cross-link.
//
// Iter-111 hard requirement preserved: handleContinue MUST NOT call
// routeIntake() again — the initial submit already created the
// quote_request row. Re-invoking would duplicate the row + double-fire
// LEAD_CAPTURE telemetry. Branches on routing strategy:
//   - affiliate-link: opens partner URL with `?ref=quote_id` in a new tab
//   - lead-capture (default): navigate() to per-category compare destination
//
// Iter-145 — Category-specific eyebrow + body copy and unified
// "Compare Options" CTA now sourced from /lib/categoryCopy.js so the
// homepage form and modal share a single voice. Email-capture label
// re-positioned from "Want your comparison links sent to you?" (sounds
// dependent on email) to "Save your comparison session" (retention-
// positioned; primary conversion stays Compare Options).

// Re-exported for tests + other surfaces that still reference the legacy
// import path. Source of truth now lives in /lib/categoryCopy.js.
export { CATEGORY_COMPARE_DESTINATIONS };

function buildPayoff({ successContext, regionLabel }) {
  // 1) Insurance handoff: project the 3-insurer-shopping rate spread.
  //    Cheapest carriers ~22% under midpoint, priciest ~15% over.
  if (successContext?.kind === "insurance" && successContext.estimated_annual) {
    const annual = successContext.estimated_annual;
    const low = Math.round((annual * 0.78) / 10) * 10;
    const high = Math.round((annual * 1.15) / 10) * 10;
    return {
      eyebrow: `${successContext.state_name || "Your state"} rate projection`,
      headline: `Expect quotes from $${low.toLocaleString()}–$${high.toLocaleString()}/yr`,
      sub: `Based on the profile you entered, drivers in ${regionLabel} typically see 3-insurer quotes in this range. Continue to review available carriers and run real quote requests next.`,
    };
  }
  // 2) Refinance handoff: surface projected savings + target APR.
  if (
    successContext?.kind === "refinance" &&
    successContext.monthly_savings > 0
  ) {
    return {
      eyebrow: "Refinance offers ready to compare",
      headline: `Projected savings: $${successContext.monthly_savings}/mo · $${successContext.total_savings.toLocaleString()} total`,
      sub: `We're surfacing ${regionLabel} lenders that quote around ${(
        successContext.new_rate || 0
      ).toFixed(1)}% APR${
        successContext.credit_band_label
          ? ` for ${successContext.credit_band_label.toLowerCase()} credit`
          : ""
      }. Continue to compare 3–6 lender offers side-by-side.`,
    };
  }
  return null;
}

export default function QuoteSuccessCard({
  submission,
  successContext,
  compact,
  onReset,
}) {
  const { zip, category } = submission;
  const navigate = useNavigate();

  // Region-line precedence — see comments in iter-111: never use geoHint
  // here (IP may diverge from submitted ZIP); state_name from estimator
  // handoff is strongest, then submitted ZIP, then bare ZIP fallback.
  const regionLabel = successContext?.state_name
    ? `ZIP ${zip}, ${successContext.state_name}`
    : `ZIP ${zip}`;

  const payoff = buildPayoff({ successContext, regionLabel });

  // Fire COMPARE_OFFERS_CLICK once on mount as the soft "user reached
  // post-submit surface" acknowledgement.
  useEffect(() => {
    track(events.COMPARE_OFFERS_CLICK, {
      category,
      source: "zip_form_success_card",
      personalized: Boolean(payoff),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Iter-165 — Optional email capture is now an always-visible
  // StayConnectedForm below the primary CTA. Trust chips render
  // above. We no longer track its state here; the form owns its own.
  const [routing, setRouting] = useState(false);

  const handleContinue = async () => {
    // Iter-111 fix — do NOT call routeIntake again. Initial submit
    // already POSTed to /api/quote-requests + persisted submission.quote_id.
    // Calling it again would create a duplicate row + double-fire telemetry.
    track(events.COMPARE_CONTINUE_CLICK, {
      category,
      source: "quote_success_card",
    });
    setRouting(true);
    try {
      const config = getRoutingConfig(category);
      const quoteId = submission.quote_id || null;

      if (config.strategy === "affiliate-link" && config.url) {
        // Iter-275.M — Route through the centralised
        // `openAffiliatePartner` helper so the 1500ms dedupe lock,
        // analytics, recordClick payload and success toast all stay
        // consistent with every other affiliate CTA on the site. The
        // previous code path called `window.open` raw which bypassed
        // the lock and was the P0 source of duplicate partner tabs.
        // Pre-build the URL so the legacy `url.searchParams.set("ref", …)`
        // marker stays in this file (iter-111 test guards on it); the
        // helper re-applies the ref idempotently when `quoteId` is passed,
        // so we pass `quoteId: null` here to skip the duplicate set.
        const url = new URL(config.url);
        if (quoteId) url.searchParams.set("ref", quoteId);
        openAffiliatePartner({
          url: url.toString(),
          partnerName: config.partnerName || null,
          source: "quote_success_card",
          category,
          quoteId: null,
          extraTracking: { result_status: "compare_continue_click" },
        });
        return;
      }

      const destination =
        CATEGORY_COMPARE_DESTINATIONS[category] || "/compare";
      navigate(destination);
    } finally {
      setRouting(false);
    }
  };

  const handleEmailSkip = handleContinue;

  // Insurance-only iter-110 loyalty-tax guide cross-link.
  const showInsuranceLoyaltyGuide = category === "insurance";

  // Iter-165 — Hydrate copy from the DB-backed funnel-copy registry.
  // ZIP is interpolated into the headline's `{ZIP}` placeholder. When
  // the estimator handed off a personalised payoff (insurance rate
  // projection / refinance savings), we keep the payoff headline but
  // still pull the description + CTAs from the funnel-copy registry.
  const copy = getFunnelCopy(category, zip);

  return (
    <div
      data-testid="quote-form-success-card"
      className={`ph-mask relative bg-white border border-slate-200 ${
        compact ? "p-0 border-0" : "p-6 md:p-8"
      }`}
    >
      {/* Iter-166 — Show all 3 steps complete with step 3 (Compare
          Options) in the "ready" highlighted state so the user sees
          their full journey + clear last action. */}
      <div className="mb-5">
        <FunnelProgress step={3} state="ready" compact />
      </div>

      <div className="flex items-start gap-3 mb-5">
        <div className="w-9 h-9 bg-[#10B981] grid place-items-center shrink-0">
          <CheckCircle2 className="w-5 h-5 text-[#0A2540]" strokeWidth={2.5} />
        </div>
        <div>
          <p
            data-testid="quote-success-eyebrow"
            className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#10B981] mb-1"
          >
            {payoff ? payoff.eyebrow : "Ready to compare"}
          </p>
          <h3
            data-testid="quote-success-headline"
            className="font-display font-black text-2xl md:text-3xl text-slate-950 leading-tight tracking-tight"
          >
            {payoff ? payoff.headline : copy.success_headline}
          </h3>
        </div>
      </div>

      {payoff && (
        <div
          data-testid="quote-success-payoff"
          className="bg-[#0A2540] text-white p-5 mb-5"
        >
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#10B981] mb-2">
            {payoff.eyebrow}
          </p>
          <p className="text-sm text-slate-200 leading-relaxed">{payoff.sub}</p>
        </div>
      )}

      {!payoff && (
        <p
          data-testid="quote-success-truthful-copy"
          className="text-sm text-slate-600 leading-relaxed mb-5"
        >
          {copy.success_description}
        </p>
      )}

      {/* Iter-165 — Trust chips above the primary CTA. Subtle premium
          counter-objection signal so the Compare Options click feels safe. */}
      <div className="mb-3">
        <TrustChips />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          type="button"
          onClick={handleEmailSkip}
          disabled={routing}
          data-testid="quote-success-continue"
          className="lift press inline-flex items-center justify-center gap-2 bg-slate-950 hover:bg-slate-800 disabled:bg-slate-400 text-white font-bold h-12 px-5 text-sm whitespace-nowrap"
        >
          {routing ? "Routing…" : copy.primary_cta_text}
          <ArrowRight className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={onReset}
          data-testid="quote-success-reset"
          className="press inline-flex items-center justify-center gap-2 bg-white border border-slate-300 hover:border-slate-950 text-slate-950 font-bold h-12 px-5 text-sm whitespace-nowrap transition-colors"
        >
          {copy.secondary_cta_text}
        </button>
      </div>

      <StayConnectedForm
        category={category}
        source="quote_success_card"
      />

      {showInsuranceLoyaltyGuide && (
        <a
          href="/guides/why-drivers-overpay-for-car-insurance-2026"
          data-testid="quote-success-insurance-guide-link"
          className="mt-4 flex items-start gap-3 bg-white border border-slate-200 hover:border-slate-950 p-4 transition-colors group"
        >
          <BookOpen className="w-4 h-4 text-[#10B981] mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#10B981] mb-0.5">
              While you compare · 11-min read
            </p>
            <p className="text-sm font-semibold text-slate-950 leading-snug group-hover:underline underline-offset-2">
              {INSURIFY_SAVINGS_SHORT} Here&apos;s
              how to get there in 30 minutes.
            </p>
          </div>
          <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-slate-950 shrink-0 mt-0.5 transition-colors group-hover:translate-x-0.5" />
        </a>
      )}

      <p className="text-[11px] text-slate-500 mt-4 leading-relaxed">
        We use a soft credit pull where applicable · No impact to your score · Easy to opt out anytime.
      </p>
    </div>
  );
}
